import { useState, useEffect, useCallback } from 'react'
import { pushApi } from '@/api'

type PermissionState = 'default' | 'granted' | 'denied'

export interface UsePushNotificationsReturn {
  permission: PermissionState
  isSubscribed: boolean
  isLoading: boolean
  subscribe: () => Promise<void>
  unsubscribe: () => Promise<void>
}

function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = atob(base64)
  const arr = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; i++) arr[i] = rawData.charCodeAt(i)
  return arr
}

export function usePushNotifications(): UsePushNotificationsReturn {
  const [permission, setPermission] = useState<PermissionState>(
    typeof Notification !== 'undefined' ? (Notification.permission as PermissionState) : 'default',
  )
  const [subscription, setSubscription] = useState<PushSubscription | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return
    navigator.serviceWorker.ready
      .then((reg) => reg.pushManager.getSubscription())
      .then((sub) => setSubscription(sub))
      .catch(() => undefined)
  }, [])

  const subscribe = useCallback(async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return
    setIsLoading(true)
    try {
      const perm = await Notification.requestPermission()
      setPermission(perm as PermissionState)
      if (perm !== 'granted') return

      const vapidKey = await pushApi.getVapidPublicKey()
      if (!vapidKey) return

      const reg = await navigator.serviceWorker.ready

      const existing = await reg.pushManager.getSubscription()
      if (existing) await existing.unsubscribe()

      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      })

      const json = sub.toJSON()
      await pushApi.subscribe({
        endpoint: sub.endpoint,
        p256dh: json.keys?.['p256dh'] ?? '',
        auth: json.keys?.['auth'] ?? '',
        userAgent: navigator.userAgent,
      })

      setSubscription(sub)
    } catch (err) {
      console.error('[push] subscribe failed:', err)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const unsubscribe = useCallback(async () => {
    if (!subscription) return
    setIsLoading(true)
    try {
      await pushApi.unsubscribe(subscription.endpoint)
      await subscription.unsubscribe()
      setSubscription(null)
    } catch {
      // ignore
    } finally {
      setIsLoading(false)
    }
  }, [subscription])

  return {
    permission,
    isSubscribed: subscription !== null,
    isLoading,
    subscribe,
    unsubscribe,
  }
}
